import { useState, useRef, useCallback } from "react";
import { MouseEventCallbacks, SlotKey } from "../utils/types";
import { useImmer } from "use-immer";
import { UseInteractivity } from "./types";
import { noInteractivity } from "./noInteractivity";

export const useSelectable : UseInteractivity = ({
  onSelect = (start: SlotKey, end: SlotKey) => {},
}) => {
  const inertia = useRef<"up" | "down">("up");
  const [selection, setSelection] = useImmer<{
    start: number | null; 
    end: number | null 
  }>({ 
    start: null, 
    end: null 
  });

  const [isTouchSelecting, setIsTouchSelecting] = useState(false);
  const [isMouseSelecting, setIsMouseSelecting] = useState(false);

  const isSelected = useCallback(
    (key: number) : boolean => {
      if(!selection.start || !selection.end) return false;
      return key >= selection.start && key <= selection.end;
    },
    [selection]
  );

  const callbacks = useCallback(
    (key: number): MouseEventCallbacks => ({
      onTouchStart: () => {
        if(isTouchSelecting) {
          setIsTouchSelecting(false);
          const data = { 
            start: Math.min(selection.start!, key),
            end: Math.max(selection.start!, key)
          }
          onSelect(data.start, data.end);
          setSelection({ start: null, end: null });
          setIsTouchSelecting(false);
        } else {
          setIsTouchSelecting(true);
          setSelection({ start: key, end: key });
        }
      },

      onTouchMove: () => {
        // If the user drags their finger, cancel the current touch selection
        if (isTouchSelecting) {
          setSelection({ start: null, end: null });
          setIsTouchSelecting(false);
        }
      },

      onMouseDown: (event: React.MouseEvent<HTMLDivElement>) => {
        if (event.buttons === 1) {
          setIsMouseSelecting(true);
          setSelection({ start: key, end: key });
        }
      },

      onMouseOver: (event: React.MouseEvent<HTMLDivElement>) => {
        if (event.buttons === 1) {
          setSelection((draft) => {
            if(draft.start! <= key && key <= draft.end!) {
              if(inertia.current === "up") {
                draft.end = key;
              } else if(inertia.current === "down") {
                draft.start = key;
              }
            } else if (key < draft.start!) {
              inertia.current = "down";
              draft.start = key;
            } else if (draft.end! < key) {
              inertia.current = "up";
              draft.end = key;
            }
          });
        }
      },

      onMouseUp: (event: React.MouseEvent<HTMLDivElement>) => {
        if (isMouseSelecting) {
          onSelect(selection.start!, selection.end!);
          setSelection({ start: null, end: null });
          setIsMouseSelecting(false);
        }
      },
    }), 
    [selection, onSelect, isMouseSelecting, isTouchSelecting]
  );

  return {...noInteractivity, isSelected, callbacks};
}